import { create } from 'zustand';

const STORAGE_PREFIX = 'tripPlansByRoom';
const LEGACY_STORAGE_KEY = STORAGE_PREFIX; // previous single-key storage
const ACTIVE_USER_KEY = `${STORAGE_PREFIX}:activeUser`;
const LAST_USER_KEY = `${STORAGE_PREFIX}:lastUser`;

const normalizeUserKey = (username) => {
    if (!username) return null;
    const u = String(username).trim();
    return u.length ? u : null;
};

const storageKeyForUser = (username) => {
    const u = normalizeUserKey(username);
    return u ? `${STORAGE_PREFIX}:${u}` : `${STORAGE_PREFIX}:__anon__`;
};

const loadActiveUserFromStorage = () => {
    if (typeof localStorage === 'undefined') return null;
    try {
        return normalizeUserKey(localStorage.getItem(ACTIVE_USER_KEY));
    } catch {
        return null;
    }
};

const loadTripPlans = (username) => {
    if (typeof localStorage === 'undefined') return {};
    try {
        const key = storageKeyForUser(username);
        const raw = localStorage.getItem(key);
        if (raw) {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed : {};
        }

        // Optional legacy migration:
        // Only migrate legacy cache if we can confidently associate it with the same user.
        const u = normalizeUserKey(username);
        const lastUser = normalizeUserKey(localStorage.getItem(LAST_USER_KEY));
        if (u && lastUser && lastUser === u) {
            const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
            if (!legacyRaw) return {};
            const legacyParsed = JSON.parse(legacyRaw);
            const legacyData = legacyParsed && typeof legacyParsed === 'object' ? legacyParsed : {};
            // Migrate into user-scoped key (keep legacy key for safety)
            localStorage.setItem(key, JSON.stringify(legacyData || {}));
            return legacyData || {};
        }

        return {};
    } catch (e) {
        console.error('Failed to load trip plans from storage', e);
        return {};
    }
};

const saveTripPlans = (username, data) => {
    if (typeof localStorage === 'undefined') return;
    try {
        const u = normalizeUserKey(username);
        const key = storageKeyForUser(u);
        localStorage.setItem(key, JSON.stringify(data || {}));
        if (u) {
            localStorage.setItem(LAST_USER_KEY, u);
        }
    } catch (e) {
        console.error('Failed to save trip plans to storage', e);
    }
};

const clearTripPlansStorage = (username) => {
    if (typeof localStorage === 'undefined') return;
    try {
        const key = storageKeyForUser(username);
        localStorage.removeItem(key);
    } catch {
        // no-op
    }
};

const isSameChatRoomsSummary = (prev, next) => {
    if (prev === next) return true;
    if (!Array.isArray(prev) || !Array.isArray(next)) return false;
    if (prev.length !== next.length) return false;

    const normalizeText = (v) => {
        if (v === undefined || v === null) return '';
        return String(v);
    };

    const normalizeTime = (v) => {
        if (v === undefined || v === null) return '';
        // 서버가 ISO string/epoch 등을 줄 수 있어 stringify로만 비교
        return String(v);
    };

    const getLastMessagePreview = (room) => {
        return (
            room?.lastMessagePreview ??
            room?.lastMessage ??
            room?.lastChatMessage ??
            room?.lastMsg ??
            room?.lastMessageText ??
            room?.lastMessageContent ??
            room?.lastMessageBody ??
            ''
        );
    };

    const getLastMessageTime = (room) => {
        return (
            room?.lastMessageTime ??
            room?.lastMessageAt ??
            room?.updatedAt ??
            room?.updated_at ??
            ''
        );
    };

    for (let i = 0; i < prev.length; i++) {
        const a = prev[i];
        const b = next[i];
        const aId = a?.roomId ?? a?.id;
        const bId = b?.roomId ?? b?.id;
        if (String(aId) !== String(bId)) return false;
        if ((a?.unreadCount ?? 0) !== (b?.unreadCount ?? 0)) return false;
        if ((a?.name ?? '') !== (b?.name ?? '')) return false;
        if ((a?.imageUrl ?? '') !== (b?.imageUrl ?? '')) return false;

        // 마지막 메시지 프리뷰/시간이 바뀌면 UI가 즉시 갱신되어야 함
        if (normalizeText(getLastMessagePreview(a)) !== normalizeText(getLastMessagePreview(b))) return false;
        if (normalizeTime(getLastMessageTime(a)) !== normalizeTime(getLastMessageTime(b))) return false;
    }

    return true;
};

const useDataStore = create((set, get) => ({
    // States
    weeklyRanking: null,
    rankingLastFetched: null,
    chatRooms: null,
    chatRoomsLastFetched: null,
    activeTripPlansUser: loadActiveUserFromStorage(),
    tripPlansByRoom: loadTripPlans(loadActiveUserFromStorage()),
    isChatWizardActive: false,

    // Cache: last message / notices
    lastMessagesByRoomId: {},
    lastMessagesFetchedAtByRoomId: {},
    noticesByRoomId: {},
    noticesFetchedAtByRoomId: {},

    // Actions
    setWeeklyRanking: (data) => set({
        weeklyRanking: data,
        rankingLastFetched: Date.now()
    }),
    setChatRooms: (data) => set((state) => {
        if (isSameChatRoomsSummary(state.chatRooms, data)) {
            return state;
        }
        return {
            chatRooms: data,
            chatRoomsLastFetched: Date.now()
        };
    }),
    setIsChatWizardActive: (isActive) => set({
        isChatWizardActive: Boolean(isActive),
    }),

    setActiveTripPlansUser: (username) => set(() => {
        const nextUser = normalizeUserKey(username);
        try {
            if (typeof localStorage !== 'undefined') {
                if (nextUser) localStorage.setItem(ACTIVE_USER_KEY, nextUser);
                else localStorage.removeItem(ACTIVE_USER_KEY);
            }
        } catch {
            // no-op
        }

        return {
            activeTripPlansUser: nextUser,
            tripPlansByRoom: loadTripPlans(nextUser),
        };
    }),

    clearTripPlansByRoom: () => set((state) => {
        clearTripPlansStorage(state.activeTripPlansUser);
        return { tripPlansByRoom: {} };
    }),
    setLastMessageForRoom: (roomId, lastMessage) => set((state) => {
        const key = roomId === undefined || roomId === null ? null : String(roomId);
        if (!key) return state;
        return {
            lastMessagesByRoomId: {
                ...(state.lastMessagesByRoomId || {}),
                [key]: lastMessage,
            },
            lastMessagesFetchedAtByRoomId: {
                ...(state.lastMessagesFetchedAtByRoomId || {}),
                [key]: Date.now(),
            },
        };
    }),
    setLastMessagesBulk: (entries) => set((state) => {
        if (!entries || typeof entries !== 'object') return state;
        const nextLast = { ...(state.lastMessagesByRoomId || {}) };
        const nextAt = { ...(state.lastMessagesFetchedAtByRoomId || {}) };
        const now = Date.now();

        Object.entries(entries).forEach(([roomId, msg]) => {
            if (!roomId) return;
            nextLast[String(roomId)] = msg;
            nextAt[String(roomId)] = now;
        });

        return {
            lastMessagesByRoomId: nextLast,
            lastMessagesFetchedAtByRoomId: nextAt,
        };
    }),
    setNoticesForRoom: (roomId, notices) => set((state) => {
        const key = roomId === undefined || roomId === null ? null : String(roomId);
        if (!key) return state;
        return {
            noticesByRoomId: {
                ...(state.noticesByRoomId || {}),
                [key]: Array.isArray(notices) ? notices : [],
            },
            noticesFetchedAtByRoomId: {
                ...(state.noticesFetchedAtByRoomId || {}),
                [key]: Date.now(),
            },
        };
    }),
    setTripPlanForRoom: (roomId, plan) => set((state) => {
        const key = roomId === undefined || roomId === null ? null : String(roomId);
        if (!key) return { tripPlansByRoom: state.tripPlansByRoom };
        const nextPlans = {
            ...(state.tripPlansByRoom || {}),
            [key]: plan
        };
        saveTripPlans(state.activeTripPlansUser, nextPlans);
        return {
            tripPlansByRoom: nextPlans
        };
    }),
}));

export default useDataStore;
